import * as React from 'react';

import { cn } from '../lib/cn.js';

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="relative w-full overflow-x-auto">
      <table
        ref={ref}
        className={cn('w-full caption-bottom border-collapse text-[14px]', className)}
        {...props}
      />
    </div>
  ),
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn('border-b border-jq-border bg-jq-bg-muted/50', className)}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&>tr:last-child]:border-0', className)} {...props} />
));
TableBody.displayName = 'TableBody';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'border-b border-jq-border transition-colors duration-150 ease-jq hover:bg-jq-bg-muted/40',
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = 'TableRow';

interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc' | null;
  align?: 'left' | 'right' | 'center';
}

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, sortable, sortDirection, align = 'left', children, onClick, ...props }, ref) => {
    const alignClass =
      align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
    return (
      <th
        ref={ref}
        scope="col"
        aria-sort={
          sortable
            ? sortDirection === 'asc'
              ? 'ascending'
              : sortDirection === 'desc'
                ? 'descending'
                : 'none'
            : undefined
        }
        className={cn(
          'h-11 px-4 align-middle font-sans text-[11px] font-semibold uppercase tracking-[0.14em] text-jq-fg-2',
          alignClass,
          sortable && 'cursor-pointer select-none transition-colors hover:text-jq-fg-1',
          className,
        )}
        onClick={sortable ? onClick : undefined}
        {...props}
      >
        {sortable ? (
          <span
            className={cn(
              'inline-flex items-center gap-1.5',
              align === 'right' && 'flex-row-reverse',
            )}
          >
            {children}
            <SortGlyph direction={sortDirection ?? null} />
          </span>
        ) : (
          children
        )}
      </th>
    );
  },
);
TableHead.displayName = 'TableHead';

interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'right' | 'center';
  muted?: boolean;
  mono?: boolean;
}

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, align = 'left', muted, mono, ...props }, ref) => {
    const alignClass =
      align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
    return (
      <td
        ref={ref}
        className={cn(
          'h-12 px-4 align-middle text-[14px] text-jq-fg-1',
          alignClass,
          muted && 'text-jq-fg-2',
          mono && 'font-mono text-[13px]',
          className,
        )}
        {...props}
      />
    );
  },
);
TableCell.displayName = 'TableCell';

const TableEmpty = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & { colSpan: number; message?: string }
>(({ colSpan, message = 'No hay datos para mostrar.', className, children, ...props }, ref) => (
  <tr ref={ref} className={cn('hover:bg-transparent', className)} {...props}>
    <td colSpan={colSpan} className="px-4 py-12 text-center text-[13px] text-jq-fg-2">
      {children ?? message}
    </td>
  </tr>
));
TableEmpty.displayName = 'TableEmpty';

function SortGlyph({ direction }: { direction: 'asc' | 'desc' | null }) {
  return (
    <span aria-hidden className="inline-flex flex-col leading-none">
      <svg
        viewBox="0 0 8 4"
        className={cn(
          'h-[6px] w-[8px] transition-colors',
          direction === 'asc' ? 'text-jq-gold-deep' : 'text-jq-stone',
        )}
      >
        <title>asc</title>
        <path d="M0 4L4 0L8 4Z" fill="currentColor" />
      </svg>
      <svg
        viewBox="0 0 8 4"
        className={cn(
          'mt-[2px] h-[6px] w-[8px] transition-colors',
          direction === 'desc' ? 'text-jq-gold-deep' : 'text-jq-stone',
        )}
      >
        <title>desc</title>
        <path d="M0 0L4 4L8 0Z" fill="currentColor" />
      </svg>
    </span>
  );
}

export { Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow };
